/**
 * 認証ストア（Zustand）。
 * Cognito Hosted UI + PKCE での認証フローを管理する（D3 §5）。
 */
import { create } from 'zustand';
import type { CurrentUser } from '@/types/user';

interface AuthState {
  initializing: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  idToken: string | null;
  currentUser: CurrentUser | null;

  initialize: () => Promise<void>;
  setTokens: (tokens: {
    access_token: string;
    id_token: string;
    refresh_token: string;
  }) => void;
  clear: () => void;
}

function decodeUserFromAccessToken(token: string): CurrentUser | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      id: decoded['custom:user_id'] ?? '',
      cognito_sub: decoded.sub ?? '',
      email: decoded.email ?? '',
      name: decoded.name ?? decoded.email ?? '',
      is_feedbacker: decoded['custom:is_feedbacker'] === 'true',
      is_admin: decoded['custom:is_admin'] === 'true',
    };
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  initializing: true,
  isAuthenticated: false,
  accessToken: null,
  idToken: null,
  currentUser: null,

  initialize: async () => {
    const token = sessionStorage.getItem('access_token');
    const idToken = sessionStorage.getItem('id_token');
    if (token) {
      const user = decodeUserFromAccessToken(token);
      if (user && user.id) {
        set({
          accessToken: token,
          idToken,
          currentUser: user,
          isAuthenticated: true,
          initializing: false,
        });
        return;
      }
      sessionStorage.clear();
    }
    set({ initializing: false });
  },

  setTokens: (tokens) => {
    sessionStorage.setItem('access_token', tokens.access_token);
    sessionStorage.setItem('id_token', tokens.id_token);
    sessionStorage.setItem('refresh_token', tokens.refresh_token);
    const user = decodeUserFromAccessToken(tokens.access_token);
    set({
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      currentUser: user,
      isAuthenticated: !!user && !!user.id,
    });
  },

  clear: () => {
    sessionStorage.clear();
    set({
      accessToken: null,
      idToken: null,
      currentUser: null,
      isAuthenticated: false,
    });
  },
}));
